import { useState, useCallback, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { ExternalServiceApiTokenRow } from '../ExternalServiceApiTokenRow'
import { useExternalServiceApiTokenRepository } from '../../../../services/adaptors/externalServiceApiTokenRepositoryAdaptor'
import {
  CreateExternalServiceApiTokenInput,
  ExternalServiceApiTokenScope,
} from '../../../../domain/entity/ExternalServiceApiTokenEntity'
import store from '../../../../store'
import { addScreenMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'

type CreateTokenInput = {
  serviceName: string
  expiredAt: number
  scopes: ExternalServiceApiTokenScope[]
}

type UseExternalServiceApiTokenDataReturn = {
  rowData: ExternalServiceApiTokenRow[]
  loading: boolean
  onReload: () => Promise<void>
  onCreateToken: (input: CreateTokenInput) => Promise<string | null>
  onRevokeToken: (uuid: string) => Promise<boolean>
}

export const useExternalServiceApiTokenData = (
  functionUuid: string
): UseExternalServiceApiTokenDataReturn => {
  const [rowData, setRowData] = useState<ExternalServiceApiTokenRow[]>([])
  const [loading, setLoading] = useState(false)
  const repository = useExternalServiceApiTokenRepository()

  const onReload = useCallback(async () => {
    setLoading(true)
    try {
      const tokens = await repository.getAll()
      const rows: ExternalServiceApiTokenRow[] = tokens.map(
        ({
          uuid,
          serviceName,
          scopes,
          expiredAt,
          createdAt,
          revoked,
          expired,
        }) => ({
          uuid,
          serviceName,
          scopes,
          expiredAt,
          createdAt,
          revoked,
          expired,
        })
      )
      setRowData(rows)
    } catch (error) {
      store.dispatch(
        addScreenMessage(functionUuid, {
          type: MessageLevel.ERROR,
          title: intl.formatMessage({
            id: 'externalServiceApiToken.error.load',
          }),
        })
      )
    } finally {
      setLoading(false)
    }
  }, [repository, functionUuid])

  const onCreateToken = useCallback(
    async ({
      serviceName,
      expiredAt,
      scopes,
    }: CreateTokenInput): Promise<string | null> => {
      setLoading(true)
      try {
        const createInput: CreateExternalServiceApiTokenInput = {
          uuid: uuidv4(),
          serviceName,
          expiredAt,
          scopes,
        }
        const { rawToken } = await repository.create(createInput)
        await onReload()
        store.dispatch(
          addScreenMessage(functionUuid, {
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({
              id: 'externalServiceApiToken.success.create',
            }),
          })
        )
        return rawToken
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error'
        const isDuplicate = errorMessage.includes('duplicated')
        store.dispatch(
          addScreenMessage(functionUuid, {
            type: MessageLevel.ERROR,
            title: intl.formatMessage({
              id: isDuplicate
                ? 'externalServiceApiToken.error.duplicateServiceName'
                : 'externalServiceApiToken.error.create',
            }),
          })
        )
        return null
      } finally {
        setLoading(false)
      }
    },
    [repository, functionUuid, onReload]
  )

  const onRevokeToken = useCallback(
    async (uuid: string): Promise<boolean> => {
      setLoading(true)
      try {
        await repository.revoke(uuid)
        await onReload()
        store.dispatch(
          addScreenMessage(functionUuid, {
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({
              id: 'externalServiceApiToken.success.revoke',
            }),
          })
        )
        return true
      } catch (error) {
        store.dispatch(
          addScreenMessage(functionUuid, {
            type: MessageLevel.ERROR,
            title: intl.formatMessage({
              id: 'externalServiceApiToken.error.revoke',
            }),
          })
        )
        return false
      } finally {
        setLoading(false)
      }
    },
    [repository, functionUuid, onReload]
  )

  useEffect(() => {
    onReload()
  }, [onReload])

  return {
    rowData,
    loading,
    onReload,
    onCreateToken,
    onRevokeToken,
  }
}
